import { SBadge } from "@superwall-me/sui"
import React, { FC, ReactNode, useState } from "react"
import { CopyToClipboard } from "react-copy-to-clipboard"
import { CgSpinnerAlt } from "react-icons/cg"
import { FiCheck, FiCopy } from "react-icons/fi"

export const V2 = () => {
  return (
    <SBadge>
      <a
        href="https://docs.superwall.me/docs/event-triggers-v2"
        target={`_blank`}
        rel="noopener noreferrer"
      >{`V2`}</a>
    </SBadge>
  )
}

export const A: FC<
  React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>
> = ({ children, ...props }) => {
  let addClassNames = ""
  if (props.hasOwnProperty("className")) {
    addClassNames = props.className || ""
    delete props.className
  }
  const loading = false
  return (
    <a
      {...props}
      className={`${addClassNames}
      px-4 py-2 text-sm font-medium
      bg-accent-dark rounded-xl text-brand-primary
      hover:bg-opacity-30 focus:outline-none focus-visible:ring-2
      focus-visible:ring-white focus-visible:ring-opacity-75`}
    >
      {loading ? <CgSpinnerAlt className="animate-spin" /> : children}
    </a>
  )
}

export const Code: FC<{ code: String }> = ({ code }) => {
  const [justCopied, setJustCopied] = useState<boolean>(false)

  return (
    <div className={`bg-grayscale-light rounded-xl mb-3 w-full py-3 px-3 text-white`}>
      <CopyToClipboard
        text={code}
        onCopy={() => {
          if (!justCopied) {
            setJustCopied(true)
            setTimeout(() => {
              setJustCopied(false)
            }, 900)
          }
        }}
      >
        <div className={`flex flex-row justify-between items-center cursor-pointer`}>
          <pre className={` overflow-x-auto`}>{code}</pre>
          <div className={`hover:opacity-75 w-8`}>
            {justCopied ? <FiCheck className={`text-accent-light`} /> : <FiCopy />}
          </div>
        </div>
      </CopyToClipboard>
    </div>
  )
}

export const CopyButton: FC<{
  title?: String
  copiableText: string
  className?: string
}> = ({ title, copiableText, className }) => {
  const [justCopied, setJustCopied] = useState<boolean>(false)

  return (
    <div className={className}>
      <div className={`text-sm p-[10px] flex flex-row items-center text-white`}>
        <CopyToClipboard
          text={copiableText}
          onCopy={() => {
            if (!justCopied) {
              setJustCopied(true)
              setTimeout(() => {
                setJustCopied(false)
              }, 2000)
            }
          }}
        >
          <div className={`hover:opacity-75 px-2 cursor-pointer text-brand-primary font-semibold`}>
            {justCopied ? (
              <span className="flex flex-row items-center justify-center gap-2 text-white opacity-50">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                  <title>o-check</title>
                  <g fill="currentColor">
                    <path
                      d="M15.854,3.146l-1-1c-.178-.179-.461-.195-.66-.041L5.494,8.871,1.8,6.1c-.197-.15-.478-.13-.653,.046L.146,7.146c-.183,.183-.196,.475-.03,.674l5,6c.09,.108,.221,.173,.361,.179,.008,0,.015,0,.022,0,.132,0,.26-.052,.354-.146L15.854,3.854c.195-.195,.195-.512,0-.707Z"
                      fill="currentColor"
                    ></path>
                  </g>
                </svg>
                <span className="hidden lg:inline-block">Copied</span>
              </span>
            ) : (
              <span className="flex flex-row items-center justify-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16">
                  <title>ungroup</title>
                  <g fill="currentColor">
                    <path
                      fill="currentColor"
                      d="M11,12H1c-0.553,0-1-0.447-1-1V1c0-0.552,0.447-1,1-1h10c0.553,0,1,0.448,1,1v10C12,11.553,11.553,12,11,12z "
                    ></path>{" "}
                    <path
                      data-color="color-2"
                      d="M15,16H4v-2h10V4h2v11C16,15.553,15.553,16,15,16z"
                    ></path>
                  </g>
                </svg>

                {title}
              </span>
            )}
          </div>
        </CopyToClipboard>
      </div>
    </div>
  )
}

export const InlineCode: FC<{
  code?: String | ReactNode
  title?: String
  copiableText: string
  className?: string
}> = ({ title, code, copiableText, className }) => {
  const [justCopied, setJustCopied] = useState<boolean>(false)

  return (
    <div className={className}>
      {title && <p className="ml-2 px-3 pb-2 font-mono text-grayscale-lightest text-xs">{title}</p>}
      <div
        className={`bg-grayscale-darker rounded-xl text-sm py-3 px-3 flex grid ${
          code ? " grid-cols-[1fr,auto,auto]" : "grid-cols-[1fr]"
        } items-center text-white border border-brand-primary border-opacity-7`}
      >
        {code && <pre className={`px-2 overflow-x-auto`}>{code}</pre>}
        {code && <div className="bg-white w-[1px] h-5 mx-3 opacity-7"></div>}
        <CopyToClipboard
          text={copiableText}
          onCopy={() => {
            if (!justCopied) {
              setJustCopied(true)
              setTimeout(() => {
                setJustCopied(false)
              }, 2000)
            }
          }}
        >
          <div className={`hover:opacity-75 px-2 cursor-pointer text-brand-primary font-semibold`}>
            {justCopied ? (
              <span className="flex flex-row items-center justify-center gap-2 text-white opacity-50">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                  <title>o-check</title>
                  <g fill="currentColor">
                    <path
                      d="M15.854,3.146l-1-1c-.178-.179-.461-.195-.66-.041L5.494,8.871,1.8,6.1c-.197-.15-.478-.13-.653,.046L.146,7.146c-.183,.183-.196,.475-.03,.674l5,6c.09,.108,.221,.173,.361,.179,.008,0,.015,0,.022,0,.132,0,.26-.052,.354-.146L15.854,3.854c.195-.195,.195-.512,0-.707Z"
                      fill="currentColor"
                    ></path>
                  </g>
                </svg>
                <span className="hidden lg:inline-block">Copied</span>
              </span>
            ) : (
              <span className="flex flex-row items-center justify-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16">
                  <title>ungroup</title>
                  <g fill="currentColor">
                    <path
                      fill="currentColor"
                      d="M11,12H1c-0.553,0-1-0.447-1-1V1c0-0.552,0.447-1,1-1h10c0.553,0,1,0.448,1,1v10C12,11.553,11.553,12,11,12z "
                    ></path>{" "}
                    <path
                      data-color="color-2"
                      d="M15,16H4v-2h10V4h2v11C16,15.553,15.553,16,15,16z"
                    ></path>
                  </g>
                </svg>

                <span className={code ? "hidden lg:inline-block" : ""}>
                  {code ? "Copy" : "Copy All"}
                </span>
              </span>
            )}
          </div>
        </CopyToClipboard>
      </div>
    </div>
  )
}
